<template>
  <ModalConfirmation
    :class="$style.modal"
    hasBlur
    :isDisabledPrimary="!optIn"
    :primaryButtonProps="$options.primaryButtonProps"
    :showModal="showModal"
    title="Apply New Password Requirements"
    @cancel="handleCancel"
    @close="handleClose"
    @save="handleSave"
  >
    <template v-slot:body>
      <div
        :class="$style.body"
      >
        <div>
          <div :class="$style.instructions">
            <ParagraphBody>
              {{ $options.labels.bodyIntro }}
            </ParagraphBody>
            <HeadingSmall>
              {{ formattedDate }}
            </HeadingSmall>
            <ParagraphBody>
              {{ $options.labels.bodyFollowUp }}
              <Button
                hasNoBackground
                isExternal
                text="Learn more"
                type="informational"
                @click="handleKbLinkClicked"
              />
            </ParagraphBody>
            <ParagraphBody v-if="showEmailMessage">
              {{ emailMessage }}
            </ParagraphBody>
          </div>
          <Checkbox
            v-model="optIn"
            :label="$options.labels.optIn"
          />
        </div>
        <DatePickerInline
          v-model="date"
          preventPastDates
          showTimePicker
        />
      </div>
    </template>
  </ModalConfirmation>
</template>

<script>
import {
  Button,
  Checkbox,
  DatePickerInline,
  HeadingSmall,
  ModalConfirmation,
  ParagraphBody,
  Types,
} from '@jumpcloud/ui-components';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import Util from 'client/util/Util';

const { openUrl } = Util;
const dayOffset = 14;
const labels = {
  bodyIntro: 'When you apply this update, all users in this organization are required to reset their password by',
  bodyFollowUp: 'If a user doesn’t reset their password by this date, they may be locked out of their device until they create a new password that meets the new complexity requirements.',
  optIn: 'Require users in your organization to reset their passwords by the date above.',
};
const locale = 'en-US';
const today = new Date();
const defaultDate = new Date(today.setDate(today.getDate() + dayOffset));
const { PrimaryButtonProps } = Types;
const primaryButtonProps = PrimaryButtonProps({
  text: 'apply',
});

export default {
  name: 'ModalPasswordRequirements',

  components: {
    Button,
    Checkbox,
    DatePickerInline,
    HeadingSmall,
    ModalConfirmation,
    ParagraphBody,
  },

  props: {
    showEmailMessage: Boolean,
    showModal: Boolean,
    showSmallOrganizationMessage: Boolean,
  },

  data() {
    return {
      date: defaultDate,
      optIn: false,
    };
  },

  computed: {
    emailMessage() {
      let audience = 'administrators';

      if (this.showSmallOrganizationMessage) {
        audience = 'administrators and users';
      }

      return `Emails will be sent to all ${audience} in this organization to notify them of the change.`;
    },

    formattedDate() {
      const dateOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };

      const timeOptions = {
        hour: '2-digit',
        minute: '2-digit',
      };

      const date = this.date.toLocaleDateString(locale, dateOptions);
      const time = this.date.toLocaleTimeString(locale, timeOptions);

      return `${date} at ${time}`;
    },
  },

  methods: {
    handleCancel() {
      this.optIn = false;
      this.$emit('cancel');
    },

    handleClose() {
      this.optIn = false;
      this.$emit('close');
    },

    handleKbLinkClicked() {
      const url = KbArticleUrls.passwordSettings;

      EventHelper.triggerModalFooterKbLinkClicked({
        text: 'Read the Knowledge Base',
        url,
      });

      this.$options.openUrl(url);
    },

    handleSave() {
      this.optIn = false;
      this.$emit('save', this.date);
    },
  },

  primaryButtonProps,
  openUrl,
  labels,
};
</script>

<style module scoped>
  .modal.modal {
    --modal-width: 44rem;
    --modal-max-width: 100%;
  }

  .body {
    display: flex;
    gap: 2rem;
    padding: var(--jc-spacer) 0;
  }

  .instructions {
    margin-bottom: 4em;
  }
</style>
